import { Category } from 'shared/services/interface'
import { Locale, TranslationMap } from 'shared/i18n/interface'
import { REFINEMENT_DIMENSION } from 'shared/constants'
import { INITIAL_STATE as DEFAULT_PAGE_STATE, INITIAL_STATE } from '~/app/store/paging/constants'
import { buildFiltersAndOptions } from '~/app/store/buildFilterState'
import { DEFAULT_TENANT } from '~/app/constants'
import { getFormattedLocale } from 'shared/localeUtils'
import { buildRenderProperties } from '~/app/utils/buildRenderProperties'
import { v4 } from 'uuid'
import { buildBreadcrumbsData } from '~/app/utils/buildBreadcrumbsData'
import { isSubstrateColorFilter } from '~/app/features/Gallery/components/Filters/util'
import { StringDictionary } from 'shared/types/util'
import type { RawGalleryExperiments } from 'shared/experiments/interface'
import type { ApiResponse } from 'shared/types/gallery/api-response-with-metadata'
import type { GalleryConfigApi } from 'shared/types/gallery/config-api'
import type { GalleryModelsUrl } from 'shared/types/gallery/models'
import type { State } from 'shared/types/gallery/state'
import { RenderProperty } from 'shared/renderProperties'
import type { Care } from 'shared/types/vp/care'

type RefinementState = {
  refinements: State.RefinementState
}

export interface GalleryContext extends GalleryModelsUrl.Context {
  url: URL
  id: string
  careTilePrice?: Care.DsServicePrice
  designServicesAvailable: boolean
  locale: Locale
  config: State.ModifiedGalleryConfigState
  paging: GalleryModelsUrl.Paging
  refinements: GalleryModelsUrl.Refinements
  translations: TranslationMap
  taxonomy: StringDictionary<Category>
  quantity: GalleryModelsUrl.ValidParsedQsValue<number>
  quantityData: GalleryModelsUrl.Quantity.Data
  experimentationData: RawGalleryExperiments
  invalidCategories: string[]
  forwardedPath: string
  urlId: string
  uploadTileAvailable: boolean
  debugToolbarApis: ApiResponse[]
}

/**
 * Builds the `config` state, which is the contextual static properties used as
 * configuration values in various parts of the app that have not already been
 * dispersed into other parts of the state (i.e. global config)
 * @param context
 */
export function buildConfigState(context: GalleryContext): { [key: string]: State.ConfigState } {
  return {
    config: {
      ...context.config,
      locale: context.locale,
      name: context.id,
      market: getFormattedLocale(context.locale).region,
      mpvId: context.config.mpv,
      renderProperties: buildRenderProperties(context.config.renderProperties),
      selectedOptions: context.selectedOptions,
      tenant: DEFAULT_TENANT,
      quantity: context.quantity,
      quantityData: context.quantityData,
      url: context.url,
      forcedRankingStrategy: context.forcedRankingStrategyOverride ?? context.config.forcedRankingStrategy,
      viewId: v4(),
      designEngagementViewIds: [],
      igpLevel: context.config.metadata.igpLevel as State.IgpLevel | undefined,
      lastLoadMoreLength: 0,
      designServicesAvailable: context.designServicesAvailable,
      careTilePrice: context.careTilePrice,
      uploadTileAvailable: context.uploadTileAvailable,
      dpcId: context.dpcId,
      designConceptId: context.designConcept,
      ...(context.config?.metadata && {
        metadata: {
          ...context.config.metadata,
          ...buildBreadcrumbsData(context.config.metadata.breadcrumbs),
        },
      }),
    },
  }
}

export function buildDebugState(context: GalleryContext): { debug: State.DebugState } {
  const quickViewId = context.quickViewId || context.quickViewIdFromSearch
  return {
    debug: {
      bypassApproval: context.bypassApproval,
      debug: context.debug,
      lastRequestUrl: undefined,
      noCache: context.noCache,
      useConstraints: context.useConstraints,
      renderPropsOverride: context.renderPropsOverride,
      forcedRankingStrategyOverride: context.forcedRankingStrategyOverride,
      quickViewId,
      isDirectQuickView: !!quickViewId,
      experienceType: context.config.experienceType,
      templatePurposes: context.templatePurposes,
      sortingStrategy: context.sortingStrategy,
      searchBackend: context.searchBackend,
      source: context.source,
      designCreationTypes: context.designCreationTypes,
      aspExperimentFlags: context.aspExperimentFlags,
      isSimilarProduct: context.isSimilarProduct,
      takeOverQV: context.takeOverQV,
      selectedDesignId: context.selectedDesignId,
      showSets: context.showSets,
      rawSets: context.rawSets,
      forwardedPath: context.forwardedPath,
      urlId: context.urlId,
    },
  }
}

export function buildDebugToolbarState(context: GalleryContext): { debugToolbar: State.DebugToolbarState } {
  return {
    debugToolbar: {
      apis: context.debugToolbarApis,
    },
  }
}

export function buildExperimentationState(context: GalleryContext): { experimentation: State.ExperimentationState } {
  return {
    experimentation: {
      rawExperiments: context.experimentationData.rawExperiments,
    },
  }
}

/**
 * Builds the `paging` state properties from the content results
 * @return {State.PagingState} Facet State
 * @param page
 * @param pageSize
 * @param configuredDefaultPageSize
 */
export function buildPagingState(
  page: GalleryModelsUrl.ValidParsedQsValue<number>,
  pageSize: GalleryModelsUrl.ValidParsedQsValue<number>,
  configuredDefaultPageSize: State.RenderPropertyValue,
): { paging: Omit<State.PagingState, 'totalEntities' | 'hasMoreTemplates'> } {
  const currentPageSize = configuredDefaultPageSize ? parseInt(configuredDefaultPageSize as string, 10) : INITIAL_STATE.pageSize

  return {
    paging: {
      page: page || DEFAULT_PAGE_STATE.page,
      pageSize: pageSize || currentPageSize,
      loadMoreCount: DEFAULT_PAGE_STATE.loadMoreCount,
    },
  }
}

const buildRefinement = (value: string, dimension: string, isImplicitDefault?: boolean): State.Refinement => ({
  dimension,
  value: value.toLocaleLowerCase(),
  isImplicitDefault,
})

/**
 * Builds refinements state from the url context refinements
 * @param refinements
 * @param invalidCategories
 */
export function buildRefinementStateFromUrlRefinements(refinements: GalleryModelsUrl.Refinements, invalidCategories: string[]): State.RefinementState {
  const refState = {} as State.RefinementState

  // first add all of the refinements from the url
  if (refinements.attributes) {
    refinements.attributes.forEach((att) => {
      const ref = buildRefinement(att, REFINEMENT_DIMENSION.ATTRIBUTE)

      refState[ref.value] = ref
    })
  }

  if (refinements.categories) {
    refinements.categories
      .filter((value) => !invalidCategories.includes(value))
      .forEach((att) => {
        const ref = buildRefinement(att, REFINEMENT_DIMENSION.CATEGORY)

        refState[ref.value] = ref
      })
  }

  if (refinements.keyword) {
    refState.keyword = buildRefinement(refinements.keyword, REFINEMENT_DIMENSION.KEYWORD)
  }

  if (refinements.collection) {
    refState.collection = buildRefinement(refinements.collection, REFINEMENT_DIMENSION.COLLECTION)
  }

  return refState
}

/**
 * converts the refinements array into a refinement state object
 * @param config
 * @param {string[]} refinements
 * @param invalidCategories
 * @return {RefinementState} Refinement State
 */
export function buildRefinementState(config: GalleryConfigApi.Config, refinements: GalleryModelsUrl.Refinements, invalidCategories: string[]): RefinementState {
  const refState = buildRefinementStateFromUrlRefinements(refinements, invalidCategories)

  config.attributeFilters.forEach((f) => {
    // don't add defaults if an option for that filter was in the url
    if (f.options.find((o) => !!refState[o.value.toLocaleLowerCase()])) {
      return
    }

    const isSubstrateColor = isSubstrateColorFilter(f.type)
    const defaultOptions = f.options.filter((o) => o.isDefault)
    const optionsToApply = isSubstrateColor && !defaultOptions.length ? [f.options[0]] : defaultOptions

    optionsToApply.forEach((o) => {
      refState[o.value.toLocaleLowerCase()] = buildRefinement(o.value, REFINEMENT_DIMENSION.ATTRIBUTE, true)
    })
  })

  return { refinements: refState }
}

export function buildDesignPersonalizationState(): { designPersonalization: State.DesignPersonalizationState } {
  return {
    designPersonalization: {
      dpcsLoading: false,
      designPersonalizationContext: undefined,
      designPersonalizationContextId: undefined,
      designPersonalizationWidgetHasImage: undefined,
    },
  }
}

export function buildState(context: GalleryContext): State.GlobalState {
  return {
    ...buildConfigState(context),
    ...buildDebugState(context),
    ...buildDebugToolbarState(context),
    ...buildFiltersAndOptions(context.config, context.taxonomy),
    ...buildPagingState(context.paging.page, context.paging.pageSize, context.config.renderProperties[RenderProperty.DefaultPageSize]),
    ...buildRefinementState(context.config, context.refinements, context.invalidCategories),
    ...buildExperimentationState(context),
    ...buildDesignPersonalizationState(),
  } as State.GlobalState
}
